import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useClient } from '../connect_api';
import { MachineService, MachineInfo } from '../api/api_pb';
import RelativeTime from '../components/RelativeTime';
import { useGlobalContext } from '../GlobalContext';
import {
    Package,
    MapPin,
    Phone,
    Wifi,
    WifiOff,
    Edit2,
    Eye,
    Activity,
    Hash,
    FileText,
    Monitor,
    Settings
} from 'lucide-react';

const Lockers: React.FC = () => {
    const machineClient = useClient(MachineService);
    const [machineList, setMachineList] = useState<MachineInfo[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const { updateMachineInfos } = useGlobalContext();

    const navigate = useNavigate();

    const handleEditClick = (machine: MachineInfo) => {
        updateMachineInfos(machine); 
        navigate(`/edit-machine/${machine.uuid}`);
    };

    const handleLockerDetailsClick = (machine: MachineInfo) => {
        updateMachineInfos(machine);
        navigate(`/locker-details/${machine.uuid}`);
    };

    const handleManageActuatorsClick = (machine: MachineInfo) => {
        updateMachineInfos(machine);
        navigate(`/manage-actuators/${machine.uuid}`);
    };

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await machineClient.listMachines({});
            if (response.machines.length !== 0) {
                setMachineList(response.machines);
            }
        } catch (e) {
            toast.dismiss();
            toast.error('Failed to fetch data');
            console.log('error', e);
        } finally {
            setIsLoading(false);
        }
    }, [machineClient]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const MachineCard = ({ machine }: { machine: MachineInfo }) => (
        <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100">
            {/* Status Bar */}
            <div className={`h-2 ${machine.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
            
            {/* Card Content */}
            <div className="p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{machine.name}</h3>
                        <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                machine.isOnline 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-600'
                            }`}>
                                {machine.isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                                {machine.isOnline ? 'Online' : 'Offline'}
                            </span>
                        </div>
                    </div>
                    {machine.imageUrl && (
                        <img
                            src={machine.imageUrl}
                            alt={machine.name}
                            className="w-16 h-16 object-contain rounded-lg bg-gray-50"
                        />
                    )}
                </div>

                {/* Info Grid */}
                <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-2 text-sm">
                        <Hash className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-gray-600">UUID</span>
                            <p className="font-mono text-xs text-gray-900 break-all">{machine.uuid}</p>
                        </div>
                    </div>

                    {machine.address && (
                        <div className="flex items-start gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-gray-600">Address</span>
                                <p className="text-gray-900">{machine.address}</p>
                            </div>
                        </div>
                    )}

                    {machine.contactNumber && (
                        <div className="flex items-start gap-2 text-sm">
                            <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-gray-600">Contact</span>
                                <p className="text-gray-900">{machine.contactNumber}</p>
                            </div>
                        </div>
                    )}

                    {machine.pinCode && (
                        <div className="flex items-start gap-2 text-sm">
                            <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-gray-600">Pin Code</span>
                                <p className="text-gray-900 font-mono">{machine.pinCode}</p>
                            </div>
                        </div>
                    )}

                    {machine.lastHeartbeatAt && (
                        <div className="flex items-start gap-2 text-sm">
                            <Activity className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div className="flex-1">
                                <span className="text-gray-600">Last Heartbeat</span>
                                <p className="text-gray-900">
                                    <RelativeTime timestamp={machine.lastHeartbeatAt} />
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Screen Preview */}
                {machine.splashScreenUrl && (
                    <div className="mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                            <Monitor className="w-4 h-4" />
                            <span>Screen Preview</span>
                        </div>
                        <img
                            src={machine.splashScreenUrl}
                            alt="Screen"
                            className="w-full h-32 object-contain rounded-lg bg-gray-50"
                        />
                    </div>
                )}

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={() => handleLockerDetailsClick(machine)}
                        className="btn-primary flex items-center justify-center gap-2"
                    >
                        <Eye className="w-4 h-4"/>
                        View Details
                    </button>
                    <button
                        onClick={() => handleEditClick(machine)}
                        className="btn-secondary flex items-center justify-center gap-2"
                    >
                        <Edit2 className="w-4 h-4"/>
                        Edit
                    </button>

                    {/* Manage Actuators Button */}
                    <button
                        onClick={() => handleManageActuatorsClick(machine)}
                        className="btn-secondary flex items-center justify-center gap-2"
                    >
                        <Settings className="w-4 h-4"/>
                        Manage Actuators
                    </button>
                </div>


            </div>
        </div>
    );

    const MachineListItem = ({machine}: { machine: MachineInfo }) => (
        <div
            className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-4 border border-gray-100">
            <div className="flex items-center gap-4">
                {/* Status Indicator */}
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${machine.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
                
                {/* Main Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">{machine.name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            machine.isOnline 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                        }`}>
                            {machine.isOnline ? 'Online' : 'Offline'}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                            <Hash className="w-3 h-3" />
                            <span className="font-mono text-xs">{machine.uuid}</span>
                        </span>
                        {machine.address && (
                            <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {machine.address}
                            </span>
                        )}
                        {machine.contactNumber && (
                            <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {machine.contactNumber}
                            </span>
                        )}
                        {machine.lastHeartbeatAt && (
                            <span className="flex items-center gap-1">
                                <Activity className="w-3 h-3" />
                                <RelativeTime timestamp={machine.lastHeartbeatAt} />
                            </span>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={() => handleLockerDetailsClick(machine)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="View Details"
                    >
                        <Eye className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handleEditClick(machine)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                    >
                        <Edit2 className="w-5 h-5" />
                    </button>

                    <button
                        onClick={() => handleManageActuatorsClick(machine)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Manage Actuators"
                    >
                        <Settings className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-6 animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Lockers</h1>
                        <p className="text-gray-600">Manage your vending machine lockers</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* View Mode Toggle */}
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`px-3 py-1.5 rounded-md transition-colors ${
                                    viewMode === 'grid' 
                                        ? 'bg-white text-gray-900 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Grid
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-1.5 rounded-md transition-colors ${
                                    viewMode === 'list' 
                                        ? 'bg-white text-gray-900 shadow-sm' 
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                List
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-lg p-4 border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Lockers</p>
                            <p className="text-2xl font-bold text-gray-900">{machineList.length}</p>
                        </div>
                        <div className="p-3 bg-indigo-100 rounded-lg">
                            <Package className="w-6 h-6 text-indigo-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Online</p>
                            <p className="text-2xl font-bold text-green-600">
                                {machineList.filter(m => m.isOnline).length}
                            </p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-lg">
                            <Wifi className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Offline</p>
                            <p className="text-2xl font-bold text-gray-600">
                                {machineList.filter(m => !m.isOnline).length}
                            </p>
                        </div>
                        <div className="p-3 bg-gray-100 rounded-lg">
                            <WifiOff className="w-6 h-6 text-gray-600" />
                        </div>
                    </div>
                </div>
                {/* TODO: Implement proper uptime tracking with historical data
                    This should show actual uptime percentage over a time period (e.g., last 30 days)
                    rather than just current online/offline status
                    JIRA: Create ticket for "Add proper uptime tracking metrics to lockers dashboard"
                */}
                {/* <div className="bg-white rounded-lg p-4 border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Uptime Rate</p>
                            <p className="text-2xl font-bold text-indigo-600">
                                {machineList.length > 0 
                                    ? `${Math.round((machineList.filter(m => m.isOnline).length / machineList.length) * 100)}%`
                                    : '0%'
                                }
                            </p>
                        </div>
                        <div className="p-3 bg-indigo-100 rounded-lg">
                            <Activity className="w-6 h-6 text-indigo-600" />
                        </div>
                    </div>
                </div> */}
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading lockers...</p>
                    </div>
                </div>
            ) : machineList.length === 0 ? (
                <div className="text-center py-12">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">No Lockers Found</h2>
                    <p className="text-gray-600">You haven't added any lockers yet.</p>
                </div>
            ) : (
                <>
                    {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {machineList.map((machine) => (
                                <MachineCard key={machine.uuid} machine={machine} />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {machineList.map((machine) => (
                                <MachineListItem key={machine.uuid} machine={machine} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Lockers;
